import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { saveAdmin, sendPasswordLink } from "../../actions/admin";
import { accessLabel } from "./roleLabels";
import {
  fieldClass,
  labelClass,
  primaryButton,
  secondaryButton,
  smallButton,
} from "../template/templateLabels";

// The role with the least access, so a new user gets no more than they need
const leastRole = (roles) =>
  roles.length > 0
    ? roles.reduce((least, item) => (item.access.length < least.access.length ? item : least))
        .role
    : "";

// Adds a user (no admin) or edits one. The backend refuses a change to one's
// own role, so the picker is locked for the signed-in admin. No one sets
// another's password here: a new user is mailed an invite to set their own,
// and a user who forgot theirs is mailed a link to set a new one.
const UserForm = ({ admin, roles, isSelf, onClose, saveAdmin, sendPasswordLink }) => {
  const isNew = !admin;
  const [name, setName] = useState(isNew ? "" : admin.name);
  const [email, setEmail] = useState(isNew ? "" : admin.email);
  const [role, setRole] = useState(isNew ? leastRole(roles) : admin.role);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);

  const nameValid = name.trim() !== "";
  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const roleValid = roles.some((item) => item.role === role);
  const busy = submitting || sending;
  const canSubmit = !busy && nameValid && emailValid && roleValid;
  // A link goes to the saved email, and the backend stops the old one when it
  // changes
  const emailChanged = !isNew && email.trim().toLowerCase() !== admin.email;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrors([]);
    const body = { name: name.trim(), email: email.trim(), role };
    const result = await saveAdmin(isNew ? null : admin._id, body);
    if (result.admin) {
      // Their invite went to the old address; it goes to the new one instead
      if (emailChanged && admin.pending) await sendPasswordLink(admin._id);
      onClose();
    } else {
      setErrors(result.errors.map((error) => error.msg));
      setSubmitting(false);
    }
  };

  const sendLink = async () => {
    setSending(true);
    await sendPasswordLink(admin._id);
    setSending(false);
  };

  const close = () => {
    if (!busy) onClose();
  };

  return (
    // Only a press on the backdrop itself closes the sheet
    <div
      className="fixed inset-0 z-30 flex items-end pt-12 bg-black bg-opacity-50 sm:items-center sm:justify-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <form
        onSubmit={submit}
        className="flex flex-col w-full max-h-full overflow-hidden bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:max-w-lg"
      >
        <header className="flex items-start justify-between flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <p className="text-lg font-semibold text-gray-700 truncate dark:text-gray-300">
              {isNew ? "Шинэ хэрэглэгч" : "Хэрэглэгч засах"}
            </p>
            {!isNew && (
              <p className="text-sm text-gray-600 truncate dark:text-gray-400">
                {admin.email}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="inline-flex items-center justify-center w-6 h-6 ml-3 text-gray-400 transition-colors duration-150 rounded dark:hover:text-gray-200 hover:text-gray-700"
            aria-label="close"
          >
            <i className="fas fa-times" />
          </button>
        </header>

        <div className="min-h-0 px-6 py-4 space-y-4 overflow-y-auto">
          {errors.length > 0 && (
            <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md dark:bg-red-900 dark:text-red-100">
              {errors.map((message, i) => (
                <p key={i}>{message}</p>
              ))}
            </div>
          )}

          <label className="block">
            <span className={labelClass}>Нэр</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              autoFocus
              className={fieldClass}
            />
          </label>

          <label className="block">
            <span className={labelClass}>Имэйл</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              autoComplete="off"
              className={fieldClass}
            />
            {email !== "" && !emailValid && (
              <span className="block mt-1 text-xs text-red-600 dark:text-red-400">
                Имэйл хаяг буруу байна.
              </span>
            )}
          </label>

          {isNew ? (
            <p className="flex items-start p-3 text-xs text-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-gray-300">
              <i className="mt-0.5 mr-2 fas fa-envelope" />
              Энэ имэйл рүү урилга очно. Хэрэглэгч урилгын холбоосоор нууц үгээ
              өөрөө үүсгэж бүртгэлээ баталгаажуулна.
            </p>
          ) : (
            <div>
              <span className={labelClass}>Нууц үг</span>
              <div className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg dark:border-gray-600">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {admin.pending
                    ? "Хэрэглэгч урилгаа нээж нууц үгээ үүсгээгүй байна."
                    : "Нууц үгийг зөвхөн хэрэглэгч өөрөө үүсгэнэ. Сэргээх холбоос түүний имэйл рүү очно."}
                </p>
                <button
                  type="button"
                  onClick={sendLink}
                  disabled={busy || emailChanged}
                  className={
                    smallButton +
                    " inline-flex items-center flex-shrink-0 whitespace-nowrap" +
                    (busy || emailChanged ? " opacity-50 cursor-not-allowed" : "")
                  }
                >
                  <i className="mr-1.5 fas fa-envelope" />
                  {sending
                    ? "Илгээж байна..."
                    : admin.pending
                    ? "Урилга дахин илгээх"
                    : "Нууц үг сэргээх"}
                </button>
              </div>
              {emailChanged && (
                <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {admin.pending
                    ? "Хадгалахад урилга шинэ хаяг руу очно."
                    : "Шинэ хаягийг хадгалсны дараа сэргээх холбоос илгээх боломжтой."}
                </span>
              )}
            </div>
          )}

          <fieldset>
            <legend className={labelClass}>Эрх</legend>
            {isSelf && (
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                <i className="mr-1 fas fa-lock" />
                Өөрийн эрхийг өөрчлөх боломжгүй.
              </p>
            )}
            {!isNew && !roleValid && (
              <p className="mb-2 text-xs text-yellow-700 dark:text-yellow-300">
                Одоогийн эрх "{admin.role}" системд алга. Шинэ эрх сонгоно уу.
              </p>
            )}
            <div className="space-y-2">
              {roles.map((item) => {
                const checked = role === item.role;
                const locked = isSelf || submitting;
                return (
                  <label
                    key={item.role}
                    className={
                      "flex items-start p-3 border rounded-lg transition-colors duration-150 " +
                      (checked
                        ? "border-purple-400 bg-purple-50 dark:bg-gray-700 dark:border-purple-500"
                        : "border-gray-200 dark:border-gray-600") +
                      (locked ? " cursor-not-allowed" : " cursor-pointer") +
                      (isSelf && !checked ? " opacity-50" : "")
                    }
                  >
                    <input
                      type="radio"
                      name="role"
                      value={item.role}
                      checked={checked}
                      onChange={() => setRole(item.role)}
                      disabled={locked}
                      className="mt-1 text-purple-600 focus:ring-purple-200 focus:ring-opacity-50 focus:ring"
                    />
                    <span className="flex-1 min-w-0 ml-3">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {item.name}
                      </span>
                      <span className="flex flex-wrap gap-1 mt-1">
                        {item.access.map((key) => (
                          <span
                            key={key}
                            className="px-2 py-0.5 text-xs text-gray-600 bg-gray-100 rounded dark:text-gray-300 dark:bg-gray-800"
                          >
                            {accessLabel(key)}
                          </span>
                        ))}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>

        <footer className="flex justify-end flex-shrink-0 px-6 py-3 space-x-3 border-t border-gray-100 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className={secondaryButton}
          >
            Болих
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={
              primaryButton + (canSubmit ? "" : " opacity-50 cursor-not-allowed")
            }
          >
            {submitting
              ? isNew
                ? "Илгээж байна..."
                : "Хадгалж байна..."
              : isNew
              ? "Урилга илгээх"
              : "Хадгалах"}
          </button>
        </footer>
      </form>
    </div>
  );
};

UserForm.propTypes = {
  admin: PropTypes.object,
  roles: PropTypes.array.isRequired,
  isSelf: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  saveAdmin: PropTypes.func.isRequired,
  sendPasswordLink: PropTypes.func.isRequired,
};

export default connect(null, { saveAdmin, sendPasswordLink })(UserForm);
